import './tenant-form.css';

import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useSnackbar } from 'material-ui-snackbar-provider';
import React, { useState } from 'react';
import { Link, useHistory, withRouter } from 'react-router-dom';
import Select from 'react-select';

import { useForm } from '../../components/utils/hooks';

// ── GraphQL ────────────────────────────────────────────────
const FETCH_TENANTS_QUERY = gql`
  query {
    tenants {
      id
      domainName
      domainId
      channels
      concurrentUser
      concurrentSessions
      onHoldUsers
      isActive
      adminNotification
    }
  }
`;

const PLATFORM_TENANT_QUERY = gql`
  query Tenant($id: ID!) {
    tenant(id: $id) {
      id
      domainName
      domainId
      channels
      concurrentUser
      concurrentSessions
      onHoldUsers
      isActive
      adminNotification
      adminRol
      notificationText
      user {
        roles
        username
        id
      }
    }
    platforms {
      value
      label
    }
  }
`;

const UPDATE_TENANT = gql`
  mutation tenantUpdate($tenantInput: tenantInput) {
    tenantUpdate(tenantInput: $tenantInput)
  }
`;

const UPDATE_ROL_USER = gql`
  mutation tenantUserRolesUpdate($tenantInput: tenantInput) {
    tenantUserRolesUpdate(tenantInput: $tenantInput)
  }
`;

// ── react-select styles ────────────────────────────────────
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#84cc16' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(132, 204, 22, 0.12)' : 'none',
    borderRadius: 8,
    minHeight: 40,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    '&:hover': { borderColor: '#d1d5db' },
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#ecfccb', borderRadius: 4 }),
  multiValueLabel: (base) => ({ ...base, color: '#4d7c0f', fontSize: 12, fontWeight: 500 }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#4d7c0f',
    '&:hover': { backgroundColor: '#d9f99d', color: '#3f6212' },
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: 14 }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    backgroundColor: state.isFocused ? '#f7fee7' : 'transparent',
    color: '#111827',
  }),
};

// ── SVG icons ──────────────────────────────────────────────
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// ── Form (receives pre-fetched data) ───────────────────────
const TenantEditForm = ({ data }) => {
  const snackbar = useSnackbar();
  const history = useHistory();

  // Preserve exact original state initialisation (variable names kept to
  // maintain the same mutation argument mapping as the existing backend calls)
  const [channelsName, setchannelsName] = useState(data.tenant.channels || 'None');
  const [checkedNotify, setNotify] = useState(data.tenant.isActive);
  const [checked, setChecked] = useState(data.tenant.adminNotification);
  const [submitting, setSubmitting] = useState(false);

  const [tenantUpdate] = useMutation(UPDATE_TENANT);
  const [tenantUserRolUpdate] = useMutation(UPDATE_ROL_USER);

  const roles = data.tenant.user ? data.tenant.user.roles : [];

  const { onChange, values } = useForm(UPDATE_TENANT, {
    id: data.tenant.id,
    domainName: data.tenant.domainName,
    domainId: data.tenant.domainId,
    concurrentUser: parseInt(data.tenant.concurrentUser),
    concurrentSessions: parseInt(data.tenant.concurrentSessions),
    onHoldUsers: parseInt(data.tenant.onHoldUsers),
    notificationText: data.tenant.notificationText,
    isActive: data.tenant.isActive,
    roles,
    adminNotification: data.tenant.adminNotification,
    adminRol:
      data.tenant.adminRol === null
        ? roles.includes('admin')
        : data.tenant.adminRol
        ? data.tenant.adminRol
        : roles.includes('admin'),
  });

  function handleAdminRol(e) {
    if (values.roles.includes('admin')) {
      if (values.adminRol) {
        var r = values.roles;
        var index = r.indexOf('admin');
        r.splice(index, 1);
        onChange({ target: { name: 'roles', value: r } });
        onChange({ target: { name: e.target.name, value: false } });
      }
    } else {
      var oldRol = values.roles;
      oldRol.push('admin');
      onChange({ target: { name: 'roles', value: oldRol } });
      onChange({ target: { name: e.target.name, value: true } });
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    tenantUpdate({
      variables: {
        tenantInput: {
          id: values.id,
          domainName: values.domainName,
          domainId: values.domainId,
          channels: channelsName,
          concurrentUser: parseInt(values.concurrentUser),
          concurrentSessions: parseInt(values.concurrentSessions),
          onHoldUsers: parseInt(values.onHoldUsers),
          adminNotification: checkedNotify,
          adminRol: values.adminRol,
          isActive: checked,
          notificationText: values.notificationText,
        },
      },
      refetchQueries: [{ query: FETCH_TENANTS_QUERY }],
      awaitRefetchQueries: true,
    })
      .then(() =>
        tenantUserRolUpdate({
          variables: { tenantInput: { id: data.tenant.user.id, roles: values.roles } },
        })
      )
      .then((r) => {
        if (r.data.tenantUserRolesUpdate !== null) {
          snackbar.showMessage('Tenant updated');
          history.replace('/tenants');
        }
      })
      .catch((err) => { console.error(err); setSubmitting(false); });
  };

  // Broken-data fallback
  if (!data.tenant.user) {
    return (
      <div className="tenant-form-page">
        <div className="form-card" style={{ padding: 32 }}>
          <h3 style={{ margin: '0 0 8px', fontFamily: "'Inter', sans-serif", color: '#111827' }}>
            Tenant data is incomplete
          </h3>
          <p style={{ margin: '0 0 16px', color: '#6b7280', fontFamily: "'Inter', sans-serif", fontSize: 14 }}>
            ID: {data.tenant.id} · {data.tenant.domainName}
          </p>
          <button className="btn-ghost" onClick={() => history.push('/tenants')}>
            Back to Tenants
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-form-page">
      {/* Page header */}
      <div className="form-page-header">
        <div>
          <div className="form-breadcrumb">
            <a href="/tenants">Tenants</a>
            <span className="form-breadcrumb-sep">/</span>
            <span className="form-breadcrumb-current">Edit Tenant</span>
          </div>
          <h1 className="form-page-title">{values.domainName}</h1>
        </div>
      </div>

      {/* Meta strip */}
      <div className="tenant-meta-strip">
        <span className="tenant-meta-item">
          <span className="tenant-meta-label">Tenant ID</span>
          <code className="tenant-meta-value">{data.tenant.id}</code>
        </span>
        <span className="tenant-meta-sep" />
        <span className="tenant-meta-item">
          <span className="tenant-meta-label">
            <UserIcon /> User
          </span>
          <Link
            to={`/edit-user/${data.tenant.user.id}`}
            className="tenant-meta-link"
          >
            {data.tenant.user.username}
          </Link>
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-card">
          <div className="form-card-body">
            <p className="form-section-title">Tenant Details</p>

            <div className="form-grid">
              {/* Domain Name — read-only */}
              <div className="form-group">
                <label className="form-label" htmlFor="domainName">
                  Domain Name
                  <span className="form-locked-badge"><LockIcon /> Locked</span>
                </label>
                <input
                  className="form-input form-input-locked"
                  id="domainName"
                  name="domainName"
                  value={values.domainName || ''}
                  readOnly
                  tabIndex={-1}
                />
              </div>

              {/* Domain ID — read-only */}
              <div className="form-group">
                <label className="form-label" htmlFor="domainId">
                  Domain ID
                  <span className="form-locked-badge"><LockIcon /> Locked</span>
                </label>
                <input
                  className="form-input form-input-locked"
                  id="domainId"
                  name="domainId"
                  value={values.domainId || ''}
                  readOnly
                  tabIndex={-1}
                />
              </div>

              {/* Concurrent Users */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="concurrentUser">
                  Concurrent Users
                </label>
                <input
                  className="form-input"
                  id="concurrentUser"
                  name="concurrentUser"
                  type="number"
                  min="0"
                  value={values.concurrentUser || ''}
                  onChange={onChange}
                  required
                />
              </div>

              {/* Concurrent Sessions */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="concurrentSessions">
                  Concurrent Sessions
                </label>
                <input
                  className="form-input"
                  id="concurrentSessions"
                  name="concurrentSessions"
                  type="number"
                  min="0"
                  value={values.concurrentSessions || ''}
                  onChange={onChange}
                  required
                />
              </div>

              {/* Hold Sessions */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="onHoldUsers">
                  Hold Sessions
                </label>
                <input
                  className="form-input"
                  id="onHoldUsers"
                  name="onHoldUsers"
                  type="number"
                  min="0"
                  value={values.onHoldUsers || ''}
                  onChange={onChange}
                  required
                />
              </div>

              {/* Channels */}
              <div className="form-group">
                <label className="form-label" htmlFor="channels">
                  Channels
                </label>
                <Select
                  inputId="channels"
                  isMulti
                  name="channels"
                  value={channelsName}
                  onChange={setchannelsName}
                  options={data.platforms}
                  styles={selectStyles}
                  placeholder="Select channels…"
                  isClearable
                />
              </div>

              {/* Notification Text — full width */}
              <div className="form-group form-grid-full">
                <label className="form-label" htmlFor="notificationText">
                  Notification Text
                </label>
                <textarea
                  className="form-textarea"
                  id="notificationText"
                  name="notificationText"
                  placeholder="e.g. Welcome to our platform"
                  value={values.notificationText || ''}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="form-divider" />
            <p className="form-section-title">Settings</p>

            {/* Is Active toggle */}
            <div className="toggle-row" style={{ marginBottom: 12 }}>
              <div className="toggle-row-label">
                <span className="toggle-label-text">Active</span>
                <span className="toggle-label-desc">Tenant is enabled and accessible</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setChecked((v) => !v)}
                />
                <span className="toggle-track" />
                <span className="toggle-thumb" />
              </label>
            </div>

            {/* Checkboxes */}
            <div className="checkbox-rows">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={checkedNotify}
                  onChange={() => setNotify((v) => !v)}
                />
                <span className="checkbox-row-label">Admin Notifications</span>
              </label>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  name="adminRol"
                  checked={values.adminRol}
                  onChange={handleAdminRol}
                />
                <span className="checkbox-row-label">Admin Role</span>
              </label>
            </div>
          </div>

          <div className="form-card-footer">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => history.push('/tenants')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-form"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// ── Wrapper: fetch by ID, then render ──────────────────────
function EditTenants({ id }) {
  const { data = {}, loading, error } = useQuery(PLATFORM_TENANT_QUERY, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });

  if (loading || !data.tenant) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, fontFamily: "'Inter', sans-serif", color: '#9ca3af', gap: 10 }}>
        <span style={{ width: 18, height: 18, border: '2px solid #e5e7eb', borderTopColor: '#84cc16', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
        Loading tenant…
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, fontFamily: "'Inter', sans-serif", color: '#ef4444' }}>
        Error loading tenant: {error.message}
      </div>
    );
  }

  return <TenantEditForm data={data} />;
}

export default withRouter(EditTenants);
